import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useCookieConsentStore } from '@/stores/cookie-consent.store'
import { cn } from '@/lib/utils'

interface CookieBannerProps {
  onManage: () => void
}

/** Figma `button-md` / `button-lg`, radius/xl (24px) — the banner's own button. */
function BannerButton({
  children,
  variant,
  onClick,
}: {
  children: string
  variant: 'primary' | 'secondary'
  onClick: () => void
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'hub-label-md flex w-full cursor-pointer items-center justify-center rounded-xl px-4 py-3 whitespace-nowrap transition-colors md:w-auto lg:py-2',
        variant === 'primary'
          ? 'bg-btn-primary text-btn-text-inverse hover:opacity-90'
          : 'bg-btn-secondary-raised hub-stroke text-btn-text hover:brightness-95',
      )}
    >
      {children}
    </button>
  )
}

/**
 * Cookie consent banner — Figma `cookies-banner` (2256:281989).
 * Pinned to the bottom of the viewport; side-by-side from 900px up and
 * stacked below that, where the buttons go full width.
 */
export function CookieBanner({ onManage }: CookieBannerProps): ReactElement | null {
  const { t } = useTranslation()
  const decided = useCookieConsentStore((state) => state.decided)
  const acceptAll = useCookieConsentStore((state) => state.acceptAll)
  const rejectNonEssential = useCookieConsentStore((state) => state.rejectNonEssential)

  if (decided) return null

  return (
    <aside
      data-node-id="2256:281989"
      data-name="cookies-banner"
      aria-label={t('cookies.title')}
      className="hub-surface-cookies fixed inset-x-0 bottom-0 z-40 flex flex-col items-stretch justify-between gap-6 px-6 py-4 md:flex-row md:items-center md:gap-0 md:pr-4"
    >
      <div className="text-cookies-text flex flex-col items-start gap-1 md:min-w-px md:flex-1 lg:flex-none lg:basis-[530px]">
        <p className="hub-title-lg w-full">{t('cookies.title')}</p>
        <p className="hub-body-sm w-full">{t('cookies.description')}</p>
      </div>

      <div className="flex shrink-0 flex-col items-stretch gap-2 md:flex-row md:items-center">
        <BannerButton variant="secondary" onClick={onManage}>
          {t('cookies.manage')}
        </BannerButton>
        <BannerButton variant="secondary" onClick={rejectNonEssential}>
          {t('cookies.rejectNonEssential')}
        </BannerButton>
        <BannerButton variant="primary" onClick={acceptAll}>
          {t('cookies.acceptAll')}
        </BannerButton>
      </div>
    </aside>
  )
}
